import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";
import type { AgGridReact } from "ag-grid-react";
import type {
  CellClassParams,
  IRowNode,
  ITooltipParams,
} from "ag-grid-community";
import { apiGet } from "@/services/genesisApi/httpsMethods";
import { useSocket } from "@/context/SocketContext";
import type { Deal } from "@/types/deal";
import {
  mergePipelineSnapshot,
  type PipelineCellChange,
} from "./pipelineLiveMerge";
import { meetingColumnsForField } from "./pipelineMeetingColumns";
import { formatChangedAgo } from "./pipelineCellSave";

/** Coalesces bursts of updates (e.g. one event per row of a bulk save). */
export const PIPELINE_REFRESH_DEBOUNCE_MS = 750;
/** How long to wait before retrying while a cell editor is open. */
const EDITING_RETRY_MS = 1_000;
/** A cell changed by someone else gets a tint that eases out over this long… */
export const CHANGE_TINT_MS = 3_000;
/** …and a small dot (with an "Updated by" tooltip) for this long. */
export const CHANGE_DOT_MS = 30_000;
/** Rows this tab saved recently are refreshed but not highlighted (it's our own edit). */
const OWN_SAVE_QUIET_MS = 5_000;

type RecentChange = { at: number; by: string | null };

const cellKey = (rowId: string | undefined, colId: string) =>
  `${rowId}::${colId}`;

type UsePipelineLiveUpdatesArgs = {
  activeOnly: boolean;
  /** Pause refreshes (e.g. while a save is in flight); they resume afterwards. */
  paused: boolean;
  gridRef: RefObject<AgGridReact<Deal> | null>;
  deals: Deal[];
  initialDeals: Deal[];
  setDeals: (value: Deal[] | ((prev: Deal[]) => Deal[])) => void;
  setInitialDeals: (value: Deal[] | ((prev: Deal[]) => Deal[])) => void;
};

/**
 * Keeps the Pipeline grid in step with changes made by other people.
 *
 * Any `deal_updated` event, socket reconnect or the tab becoming visible again
 * triggers a quiet refetch of the pipeline. The snapshot is merged into the
 * working rows (local edits are kept) and the cells whose value changed get
 * a soft tint that fades out, plus a small dot saying who changed it, so the
 * shared screen stays current without toasts.
 */
export function usePipelineLiveUpdates({
  activeOnly,
  paused,
  gridRef,
  deals,
  initialDeals,
  setDeals,
  setInitialDeals,
}: UsePipelineLiveUpdatesArgs) {
  const { subscribe, suppressDealUpdateToasts, isConnected } = useSocket();

  const dealsRef = useRef(deals);
  const initialDealsRef = useRef(initialDeals);
  const activeOnlyRef = useRef(activeOnly);
  const pausedRef = useRef(paused);
  useEffect(() => {
    dealsRef.current = deals;
    initialDealsRef.current = initialDeals;
    activeOnlyRef.current = activeOnly;
    pausedRef.current = paused;
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const deferredBySaveRef = useRef(false);
  const unmountedRef = useRef(false);
  const ownSavesRef = useRef(new Map<string, number>());
  const recentRef = useRef(new Map<string, RecentChange>());
  // Who made the latest change to each row, from the socket events.
  const actorsRef = useRef(new Map<string, string>());

  const noteOwnSave = useCallback((rowId: string) => {
    ownSavesRef.current.set(rowId, Date.now());
  }, []);

  const isOwnRecentSave = (rowId: string) => {
    const savedAt = ownSavesRef.current.get(rowId);
    if (savedAt == null) return false;
    if (Date.now() - savedAt > OWN_SAVE_QUIET_MS) {
      ownSavesRef.current.delete(rowId);
      return false;
    }
    return true;
  };

  const refreshRows = useCallback(
    (rowIds: string[]) => {
      const api = gridRef.current?.api;
      if (!api || api.isDestroyed()) return;
      const rowNodes = rowIds
        .map((rowId) => api.getRowNode(rowId))
        .filter((node): node is IRowNode<Deal> => node != null);
      if (rowNodes.length > 0) api.refreshCells({ rowNodes, force: true });
    },
    [gridRef],
  );

  const markChanged = useCallback(
    (changedCells: PipelineCellChange[], addedRowIds: string[]) => {
      const api = gridRef.current?.api;
      if (!api || api.isDestroyed()) return;
      const now = Date.now();
      const touched: string[] = [];
      const record = (rowId: string, colIds: string[]) => {
        const change = { at: now, by: actorsRef.current.get(rowId) ?? null };
        for (const colId of colIds)
          recentRef.current.set(cellKey(rowId, colId), change);
        touched.push(rowId);
      };

      for (const { rowId, fields } of changedCells) {
        if (isOwnRecentSave(rowId)) continue;
        record(rowId, [
          ...new Set(fields.flatMap((f) => [f, ...meetingColumnsForField(f)])),
        ]);
      }
      const allColumns = api.getAllDisplayedColumns().map((c) => c.getColId());
      for (const rowId of addedRowIds) record(rowId, allColumns);
      if (touched.length === 0) return;

      refreshRows(touched);
      // Drop the tint, then the dot, once their time is up.
      setTimeout(() => refreshRows(touched), CHANGE_TINT_MS + 50);
      setTimeout(() => {
        const cutoff = Date.now() - CHANGE_DOT_MS;
        for (const [key, change] of recentRef.current) {
          if (change.at <= cutoff) recentRef.current.delete(key);
        }
        refreshRows(touched);
      }, CHANGE_DOT_MS + 50);
    },
    [gridRef, refreshRows],
  );

  const refresh = useCallback(async () => {
    if (pausedRef.current) {
      deferredBySaveRef.current = true;
      return;
    }
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    inFlightRef.current = true;
    const requestedActiveOnly = activeOnlyRef.current;

    try {
      const response = await apiGet<Deal[]>(
        `deals?active_only=${requestedActiveOnly}`,
      );
      if (
        unmountedRef.current ||
        !response.success ||
        !Array.isArray(response.data) ||
        // The Active/All toggle refetched in the meantime; this is stale.
        requestedActiveOnly !== activeOnlyRef.current
      ) {
        return;
      }

      if (pausedRef.current) {
        // Re-run once the save finishes (see the effect on `paused`).
        deferredBySaveRef.current = true;
        return;
      }
      const api = gridRef.current?.api;
      if (api && !api.isDestroyed() && api.getEditingCells().length > 0) {
        // Don't replace rows under an open editor; retry shortly.
        pendingRef.current = true;
        return;
      }

      const merged = mergePipelineSnapshot(
        dealsRef.current,
        initialDealsRef.current,
        response.data,
      );
      setInitialDeals(merged.initialDeals);
      setDeals(merged.deals);

      if (merged.changedCells.length > 0 || merged.addedRowIds.length > 0) {
        // Let the grid render the new row data before highlighting.
        setTimeout(
          () => markChanged(merged.changedCells, merged.addedRowIds),
          100,
        );
      }
    } finally {
      inFlightRef.current = false;
      if (pendingRef.current && !unmountedRef.current) {
        pendingRef.current = false;
        scheduleRef.current(EDITING_RETRY_MS);
      }
    }
  }, [markChanged, gridRef, setDeals, setInitialDeals]);

  const schedule = useCallback(
    (delay: number = PIPELINE_REFRESH_DEBOUNCE_MS) => {
      if (timerRef.current != null) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void refresh();
      }, delay);
    },
    [refresh],
  );
  const scheduleRef = useRef(schedule);
  useEffect(() => {
    scheduleRef.current = schedule;
  }, [schedule]);

  useEffect(() => {
    unmountedRef.current = false;
    const noteActor = (rowId: unknown, actor: unknown) => {
      if (rowId != null && typeof actor === "string" && actor) {
        actorsRef.current.set(String(rowId), actor);
      }
    };
    const unsubscribers = [
      subscribe("deal_updated", (data) => {
        const event = data as { deal_id?: number; actor?: string } | undefined;
        noteActor(event?.deal_id, event?.actor);
        schedule();
      }),
      subscribe("accordion_updated", (data) => {
        const event = data as
          { accordion_id?: number; actor?: string } | undefined;
        // Add-on rows use minus the add-on id as their row id.
        if (event?.accordion_id != null)
          noteActor(-event.accordion_id, event.actor);
        schedule();
      }),
      // Fee saves can add or clear a row's "Fees" reminder chip.
      subscribe("advisor_fees_updated", () => schedule()),
      subscribe("deal_created", () => schedule()),
      subscribe("deal_deleted", () => schedule()),
      // A reconnect may have missed events while the socket was down.
      subscribe("connect", () => schedule()),
    ];

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") schedule();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      unmountedRef.current = true;
      for (const unsubscribe of unsubscribers) unsubscribe();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [schedule, subscribe]);

  useEffect(() => suppressDealUpdateToasts(), [suppressDealUpdateToasts]);

  // A refresh deferred by a save runs once the save finishes.
  useEffect(() => {
    if (!paused && deferredBySaveRef.current) {
      deferredBySaveRef.current = false;
      schedule();
    }
  }, [paused, schedule]);

  const recentChange = (rowId: string | undefined, colId: string) => {
    const change = recentRef.current.get(cellKey(rowId, colId));
    return change && Date.now() - change.at < CHANGE_DOT_MS ? change : null;
  };

  // Read from refs when AG Grid evaluates them, so they can stay stable.
  const liveCellClassRules = useMemo(
    () => ({
      "pipeline-cell--live-changed": (params: CellClassParams<Deal>) => {
        const change = recentRef.current.get(
          cellKey(params.node.id, params.column.getColId()),
        );
        return !!change && Date.now() - change.at < CHANGE_TINT_MS;
      },
      "pipeline-cell--recent": (params: CellClassParams<Deal>) =>
        recentChange(params.node.id, params.column.getColId()) != null,
    }),
    [],
  );

  const liveTooltip = useCallback((params: ITooltipParams<Deal>) => {
    const colId =
      params.column && "getColId" in params.column
        ? params.column.getColId()
        : null;
    const change = colId ? recentChange(params.node?.id, colId) : null;
    if (!change) return undefined;
    return `Updated by ${change.by ?? "someone"}${formatChangedAgo(
      new Date(change.at).toISOString(),
    )}`;
  }, []);

  return {
    isLive: isConnected,
    refresh: schedule,
    noteOwnSave,
    liveCellClassRules,
    liveTooltip,
  };
}
